import { SubjectId } from '@master/constants/subject.constant';
import Tab, { TabProps } from '@ui/components/molecules/tab/tab.molecule';
import { clsxm } from '@ui/utils/style.util';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTextOrientationStyleFromSubjectId } from '../../utils/standard.util';
import StandardDetailBox from '../detail-box/standard-detail-box.component';
import StandardDetailQuestionBodyContent from '../detail-question-body-content/standard-detail-question-body-content.component';

// #region Types
export type StandardDetailTabProps = TabProps & {
  containerClassName?: string;
};

type StandardDetailQuestionTabProps = Pick<StandardDetailTabProps, 'items'> & {
  isTabOnly?: boolean;
  questionBodyContent?: ReactNode;
  questionBodyText?: string | null;
  questionText?: string;
  subjectId?: SubjectId;
  withQuestionBody?: boolean;
};
// #endregion

function BaseStandardDetailTab({
  className,
  containerClassName,
  itemClassName,
  ...rest
}: StandardDetailTabProps) {
  return (
    <div
      className={clsxm(
        'bg-white-teal px-7 rounded-2xl shadow-md overflow-y-auto',
        containerClassName,
      )}
    >
      <Tab
        {...rest}
        className={clsxm('w-fit', className)}
        itemClassName={clsxm('w-full px-4 pt-5 pb-2.5 text-center', itemClassName)}
      />
    </div>
  );
}

function StandardDetailQuestionTab({
  isTabOnly,
  items,
  questionBodyContent,
  questionBodyText,
  questionText,
  subjectId,
  withQuestionBody: passedWithQuestionBody,
}: StandardDetailQuestionTabProps) {
  // #region Dependencies
  const { t } = useTranslation();
  // #endregion

  // #region Computed Values
  const withQuestionBody =
    passedWithQuestionBody == null ? subjectId === SubjectId.Japanese : passedWithQuestionBody;
  // #endregion

  // #region Contents
  const tabContent = useMemo(
    () => <BaseStandardDetailTab itemClassName="w-fit pt-4 pb-2 px-4 min-w-24" items={items} />,
    [items],
  );
  // #endregion

  if (isTabOnly) return tabContent;

  return (
    <>
      {tabContent}
      {questionText && (!withQuestionBody || !!questionBodyText) ? (
        <div
          className={clsxm(
            'w-full flex flex-col gap-y-2.5 min-h-40',
            withQuestionBody && 'h-[768px] gap-y-[5px]',
          )}
          style={getTextOrientationStyleFromSubjectId(subjectId)}
        >
          {withQuestionBody && (
            <>
              <p className={subjectId === SubjectId.English ? 'text-green-dark font-bold' : ''}>
                {t('standard.label.question-body')}
              </p>
              <div className="grow w-0 basis-4/5 overflow-auto border border-grey-input-border rounded-[5px]">
                {questionBodyContent ?? (
                  <StandardDetailQuestionBodyContent className="border-0" subjectId={subjectId}>
                    {questionBodyText}
                  </StandardDetailQuestionBodyContent>
                )}
              </div>
            </>
          )}
          <p className={subjectId === SubjectId.English ? 'text-green-dark font-bold' : ''}>
            {t('standard.label.question-text')}
          </p>
          <StandardDetailQuestionBodyContent
            className={clsxm(
              'grow w-fit min-w-[166px] border rounded-[5px]',
              withQuestionBody ? 'basis-1/6' : 'py-[5px] px-2.5 bg-grey-disabled w-full',
            )}
            subjectId={subjectId}
          >
            {questionText}
          </StandardDetailQuestionBodyContent>
        </div>
      ) : (
        <StandardDetailBox.Disabled className="min-h-[246px]">
          {t('standard.info.empty-question')}
        </StandardDetailBox.Disabled>
      )}
    </>
  );
}

const StandardDetailTab = Object.assign(BaseStandardDetailTab, {
  Question: StandardDetailQuestionTab,
});

export default StandardDetailTab;
